import { motion } from "framer-motion";
import { ExternalLink, Mail, GraduationCap, Music, Heart } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const PERCORSI = [
  {
    icon: Heart,
    name: "Io Emozione",
    age: "6 – 11 anni · Scuola primaria",
    text: "Il percorso da cui nasce il quaderno: un laboratorio dove il gioco diventa consapevolezza. I bambini imparano a dare un nome alle emozioni, rafforzano l'autostima e costruiscono relazioni felici con i coetanei.",
    activities: ["Diamo un nome alle emozioni", "La Tana della Calma", "L'opera d'arte più bella del mondo", "Mindfulness – Il Pianeta delle Emozioni"],
    image: "/images/percorsi/copertina-ioemozione.jpg",
    imageFull: "/images/percorsi/ioemozione-1.jpg",
    imageAlt: "Estratto vero del percorso Io Emozione",
  },
  {
    icon: GraduationCap,
    name: "EmoProject",
    age: "11 – 14 anni · Scuola media",
    text: "L'età in cui le emozioni si fanno intense e spesso si fatica a dirle. EmoProject accompagna i ragazzi in questo passaggio: le riconoscono, le attraversano e imparano a trasformarle in risorse.",
    activities: ["FlashCards d'emozione", "EmoAlert!", "EmoStories", "Mindfulness – Chill Zone", "Il Diario di bordo"],
    image: "/images/percorsi/copertina-emoproject.jpg",
    imageFull: "/images/percorsi/emoproject-1.jpg",
    imageAlt: "Estratto vero del percorso EmoProject",
  },
  {
    icon: Music,
    name: "Sinfonie – Accordo di Suoni",
    age: "13 – 25 anni · Licei musicali e conservatori",
    text: "Per chi studia musica: le emozioni attraversano lo strumento prima ancora della musica. Un percorso per giovani musicisti che vogliono conoscere l'emozione del palco e usarla, invece di subirla.",
    activities: ["Esercizi di fiducia sensoriale", "Sostegno reciproco e collaborazione", "Visualizzazioni guidate d'ascolto"],
    image: "/images/percorsi/copertina-sinfonie.jpg",
    imageFull: "/images/percorsi/sinfonie-1.jpg",
    imageAlt: "Estratto vero del percorso Sinfonie – Accordo di Suoni",
  },
];

export const PercorsiSection = () => {
  return (
    <section id="percorsi" className="bg-white py-16 sm:py-24" data-testid="percorsi-section">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mb-12 max-w-2xl"
        >
          <span className="font-editorial text-sm italic tracking-wide text-terracotta">Per scuole e associazioni</span>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-olive sm:text-3xl lg:text-4xl" data-testid="percorsi-title">
            Porta MetodoEmo nella tua realtà
          </h2>
          <p className="mt-3 text-base text-stone-600 sm:text-lg">
            Laboratori di educazione emotiva e mindfulness per scuole, associazioni ed enti: dalla primaria ai conservatori. Nessun listino online: ogni percorso si costruisce insieme, su misura per i tuoi ragazzi. Scrivici e ti rispondiamo con una proposta.
          </p>
        </motion.div>

        <div className="mb-12 grid gap-4 sm:grid-cols-3" data-testid="percorsi-steps">
          {[
            { n: "1", t: "Ci scrivi", d: "Dal modulo in fondo alla pagina: raccontaci la tua scuola o associazione e l'età dei ragazzi." },
            { n: "2", t: "Costruiamo il percorso", d: "Numero di incontri, attività e obiettivi calibrati sulla tua realtà. Preventivo su misura." },
            { n: "3", t: "Partono i laboratori", d: "In presenza, con materiali e quaderni operativi. I ragazzi lavorano sulle emozioni giocando." },
          ].map((s) => (
            <div key={s.n} className="flex items-start gap-4 rounded-2xl border border-sand bg-cream p-5" data-testid={`percorso-step-${s.n}`}>
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-terracotta font-display text-lg font-bold text-white">{s.n}</span>
              <div>
                <p className="font-display text-base font-bold text-olive">{s.t}</p>
                <p className="mt-1 text-sm leading-relaxed text-stone-600">{s.d}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {PERCORSI.map((p, i) => (
            <motion.article
              key={p.name}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: EASE }}
              className="group flex flex-col overflow-hidden rounded-3xl border border-sand bg-cream shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              data-testid={`percorso-card-${i}`}
            >
              <a href={p.imageFull} target="_blank" rel="noopener noreferrer" className="relative block h-48 overflow-hidden" title="Tocca per vedere l'estratto vero del percorso">
                <img src={p.image} alt={p.imageAlt} className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                <span className="absolute left-4 top-4 rounded-full bg-olive/90 px-3.5 py-1.5 text-xs font-semibold text-white backdrop-blur" data-testid={`percorso-age-${i}`}>
                  {p.age}
                </span>
                <span className="absolute bottom-3 right-3 rounded-full bg-white/90 px-3 py-1 text-[11px] font-semibold text-olive shadow" data-testid={`percorso-excerpt-hint-${i}`}>
                  Estratto vero · tocca per aprire
                </span>
              </a>
              <div className="flex flex-1 flex-col p-6">
                <div className="flex items-center gap-2.5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-terracotta-light text-terracotta">
                    <p.icon className="h-4.5 w-4.5" />
                  </span>
                  <h3 className="font-display text-lg font-bold text-olive">{p.name}</h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-stone-600">{p.text}</p>
                <div className="mt-4 rounded-xl bg-white p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-terracotta">Assaggio delle attività</p>
                  <ul className="mt-2 space-y-1.5">
                    {p.activities.map((a) => (
                      <li key={a} className="flex items-start gap-2 text-sm text-stone-600">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-terracotta" />
                        {a}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-auto flex flex-wrap gap-2 pt-5">
                  <a
                    href="#contatti"
                    data-testid={`percorso-info-link-${i}`}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full bg-terracotta px-4 py-2.5 font-display text-sm font-semibold text-white transition-colors hover:bg-terracotta-dark"
                  >
                    <Mail className="h-4 w-4" /> Richiedi info e iscrizioni
                  </a>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mt-12 rounded-3xl bg-olive p-8 text-center text-white sm:p-10"
          data-testid="percorsi-cta"
        >
          <h3 className="font-display text-xl font-bold sm:text-2xl">Vuoi attivare un percorso nella tua scuola o associazione?</h3>
          <p className="mx-auto mt-2 max-w-xl text-sm text-white/80 sm:text-base">
            Scrivici dal modulo qui sotto: ti risponderemo noi, con una proposta pensata per i tuoi ragazzi. Nessun impegno, nessun listino nascosto.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <a
              href="#contatti"
              data-testid="percorsi-cta-button"
              className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3 font-display text-base font-bold text-olive shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl"
            >
              <Mail className="h-5 w-5" /> Richiedi una proposta
            </a>
            <a
              href="/files/volantino-metodoemo.pdf"
              download="MetodoEmo-Brochure.pdf"
              data-testid="percorsi-brochure-button"
              className="inline-flex items-center gap-2 rounded-full border-2 border-white/60 px-7 py-3 font-display text-base font-semibold text-white transition-colors hover:bg-white/10"
            >
              Scarica la brochure (PDF)
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
